import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/navigation/Header';
import AgentStatusPanel from '../../components/navigation/AgentStatusPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { analyticsService } from '../../services/analyticsService';
import AnalyticsStats from './components/AnalyticsStats';
import AgentPerformanceTable from './components/AgentPerformanceTable';
import PerformanceTrendsChart from './components/PerformanceTrendsChart';
import RoleFilterBar from './components/RoleFilterBar';

const AgentAnalyticsDashboard = () => {
  const navigate = useNavigate();
  const [agentPanelOpen, setAgentPanelOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedRole, setSelectedRole] = useState('all');
  const [agentMetrics, setAgentMetrics] = useState([]);
  const [teamKPIs, setTeamKPIs] = useState(null);
  const [performanceTrends, setPerformanceTrends] = useState([]);

  useEffect(() => {
    loadAnalyticsData();
  }, []);

  const loadAnalyticsData = async () => {
    try {
      setLoading(true);
      setError('');
      
      const [metrics, kpis, trends] = await Promise.all([
        analyticsService?.getAgentAnalytics(),
        analyticsService?.getTeamKPIs(),
        analyticsService?.getPerformanceTrends()
      ]);

      setAgentMetrics(metrics);
      setTeamKPIs(kpis);
      setPerformanceTrends(trends);
    } catch (err) {
      console.error('Error loading analytics:', err);
      setError(err?.message || 'Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  };

  const filteredAgents = selectedRole === 'all' 
    ? agentMetrics 
    : agentMetrics?.filter(agent => agent?.role === selectedRole);

  const handleExportReport = () => {
    const csvContent = [
      ['Agent Name', 'Role', 'Avg Response Time (s)', 'Completion Rate (%)', 'Accuracy Score (%)', 'Success Rate (%)', 'Total Analyses'],
      ...filteredAgents?.map(agent => [
        agent?.name,
        agent?.role,
        agent?.avgResponseTime,
        agent?.completionRate,
        agent?.accuracyScore,
        agent?.successRate,
        agent?.totalAnalyses
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `agent-analytics-${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    window.URL?.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-background">
      <Header onMenuClick={() => setAgentPanelOpen(!agentPanelOpen)} />
      <AgentStatusPanel 
        isOpen={agentPanelOpen} 
        onClose={() => setAgentPanelOpen(false)}
        onToggle={() => setAgentPanelOpen(!agentPanelOpen)}
      />

      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/main-dashboard')}
              className="flex items-center justify-center w-10 h-10 rounded-lg hover:bg-accent/10 transition-colors"
            >
              <Icon name="ArrowLeft" size={20} color="var(--color-foreground)" />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">Agent Analytics Dashboard</h1>
              <p className="text-sm text-muted-foreground mt-1">Track specialized agent productivity and performance metrics</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              iconName="RefreshCw"
              onClick={loadAnalyticsData}
              disabled={loading}
            >
              Refresh
            </Button>
            <Button
              variant="default"
              iconName="Download"
              onClick={handleExportReport}
              disabled={loading || filteredAgents?.length === 0}
            >
              Export Report
            </Button>
          </div>
        </div>

        {error && (
          <div className="card bg-error/10 border-error mb-6">
            <div className="flex items-center gap-3">
              <Icon name="AlertCircle" size={20} color="var(--color-error)" />
              <p className="text-error">{error}</p>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="flex flex-col items-center gap-4">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              <p className="text-muted-foreground">Loading analytics data...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-6">
            <AnalyticsStats teamKPIs={teamKPIs} agentMetrics={agentMetrics} />

            <RoleFilterBar
              selectedRole={selectedRole}
              onRoleChange={setSelectedRole}
              agentMetrics={agentMetrics}
            />

            <PerformanceTrendsChart trends={performanceTrends} />

            <AgentPerformanceTable agents={filteredAgents} />

            {filteredAgents?.length === 0 && (
              <div className="card text-center py-12">
                <Icon name="Users" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
                <p className="text-muted-foreground">No agents found for the selected role</p>
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AgentAnalyticsDashboard;